import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { UserPlus, AlertCircle } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { SearchableSelect } from "../../components/SearchableSelect";

export default function BecomeOrganizerPage() {
    const { t } = useTranslation();
    const { profile, refreshProfile } = useAuth();
    const [countries, setCountries] = useState([]);
    const [countryId, setCountryId] = useState("");
    const [organizerName, setOrganizerName] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
        api.get("/api/countries").then(({ data }) => setCountries(data || []));
    }, []);

    useEffect(() => {
        if (profile?.organizer_name) setOrganizerName(profile.organizer_name);
    }, [profile]);

    const submit = async () => {
        if (!countryId || !organizerName) {
            toast.error(t("auth.required"));
            return;
        }
        setLoading(true);
        try {
            await api.post("/api/auth/become-organizer", {
                organizer_name: organizerName,
                country_id: countryId,
            });
            toast.success(t("common.success"));
            await refreshProfile();
            navigate("/organizer/listings");
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("common.error"));
        } finally {
            setLoading(false);
        }
    };

    if (profile?.is_organizer) {
        return (
            <div className="max-w-lg mx-auto p-10 text-center">
                <p className="mb-4">{t("common.success")}</p>
                <button onClick={() => navigate("/organizer/listings")} className="btn-primary">
                    {t("nav.my_listings")}
                </button>
            </div>
        );
    }

    return (
        <div data-testid="become-organizer-page" className="max-w-lg mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="bento-card p-6 md:p-8 space-y-5">
                <div className="flex items-center gap-3 mb-2">
                    <div className="p-3 rounded-xl bg-muted">
                        <UserPlus className="h-6 w-6" />
                    </div>
                    <h1 className="font-heading font-black text-2xl md:text-3xl">{t("profile.become_organizer_title")}</h1>
                </div>
                <div className="flex items-start gap-2 p-3 rounded-xl bg-[hsl(var(--warning))]/10 text-[hsl(var(--warning))] text-xs font-semibold">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <span>{t("profile.country_locked_hint")}</span>
                </div>
                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("profile.organizer_name")} *</label>
                    <input
                        data-testid="organizer-name-input"
                        value={organizerName}
                        onChange={(e) => setOrganizerName(e.target.value)}
                        className="input-modern"
                    />
                </div>
                <div>
                    <label className="text-sm font-semibold block mb-1.5">{t("profile.organizer_country")} *</label>
                    <SearchableSelect
                        testId="organizer-country"
                        options={countries.map((c) => ({ value: c.id, label: c.name }))}
                        value={countryId}
                        onValueChange={setCountryId}
                        placeholder={t("common.choose")}
                    />
                </div>
                <button data-testid="submit-organizer" onClick={submit} disabled={loading} className="btn-primary w-full">
                    {loading ? t("common.loading") : t("profile.become_organizer_cta")}
                </button>
            </div>
        </div>
    );
}
